import { Icon, Menu, MenuItem, Position } from "@blueprintjs/core"
import CustomButton from "components/Atoms/CustomButton"
import CustomPopover from "components/Atoms/CustomPopover"
import NewTag from "components/Atoms/NewTag"
import StaticSelector from "components/Atoms/StaticSelector"
import StyledLink from "components/Atoms/StyledLink"
import OverFlowMenu from "components/Organisms/OverFlowMenu"
import DataCell, { Subtitle, Title } from "components/Organisms/table/DataCell"
import SerialColumn from "components/Organisms/table/SerialColumn"
import CustomReactTable from "components/Templates/CustomReactTable"
import PopoverTooltip from "components/Templates/PopoverTooltip"
import { USER_STATUSES } from "data/static.items"
import { useBulkUserStatusUpdate } from "graphql/user/user.hooks"
import { useVendorList } from "graphql/vendor/vendor.hooks"
import qs from "query-string"
import React from "react"
import { useNavigate } from "react-router-dom"
import AppToaster from "utils/AppToaster"
import clsx from "utils/clsx"
import { inr } from "utils/formatters.utils"
import ActiveFilters from "./ActiveFilters"
import AddAccountTransaction from "./AddAccountTransaction"
import VendorHeader from "./VendorHeader"
import VendorTransactionHistory from "./VendorTransactionHistory"
import { Prohibit } from "@phosphor-icons/react"
const COLUMNS = [
  SerialColumn,
  {
    width: 50,
    accessor: "id",
    Cell({ value, original }) {
      const accountId = original.accounts?.[0]?.id
      const navigate = useNavigate()
      return (
        <CustomPopover
          position={Position.BOTTOM_LEFT}
          minimal={true}
          content={
            <Menu>
              <MenuItem
                text="Edit Vendor"
                icon="edit"
                onClick={e =>
                  navigate(`/d/registration/vendors/details/${value}`)
                }
              />
              <MenuItem
                text="Add Transaction"
                icon="bank-account"
                onClick={e => {
                  if (!accountId) {
                    return AppToaster.show({
                      icon: "cross",
                      message: "Vendor has no account",
                      intent: "danger",
                    })
                  }
                  navigate({
                    search: qs.stringify({
                      account: accountId,
                      codename: original.accounts?.[0]?.codename,
                      balance: original.accounts?.[0]?.balance,
                    }),
                  })
                }}
              />
              <MenuItem
                text="Transaction history"
                icon="history"
                onClick={e => {
                  if (!accountId) {
                    return AppToaster.show({
                      icon: "cross",
                      message: "Vendor has no account",
                      intent: "danger",
                    })
                  }
                  navigate({
                    search: qs.stringify({
                      transaction_history: accountId,
                    }),
                  })
                }}
              />
            </Menu>
          }
        >
          <CustomButton
            style={{ transform: "rotate(90deg)" }}
            icon="more"
            minimal={true}
          />
        </CustomPopover>
      )
    },
  },
  {
    Header: "Status",
    accessor: "statusObj",
    className: "vt-vendor-status",
    width: 165,
    Cell: ({ value, original }) => {
      const [mutate, { loading }] = useBulkUserStatusUpdate()

      return (
        <StaticSelector
          loading={loading}
          onChange={(_, value) => {
            mutate({
              variables: {
                userIds: [original?.id],
                status: value?.id,
              },
            })
          }}
          items={USER_STATUSES.filter(us => /AV|NA|BS/.test(us.id))}
          value={value}
          itemRenderer={(item, { handleClick, modifiers }) => {
            return (
              <MenuItem
                icon={<Icon icon="dot" intent={item?.intent} />}
                active={modifiers.active}
                key={item.id}
                onClick={handleClick}
                text={item?.name}
                labelElement={item?.id}
              />
            )
          }}
        />
      )
    },
  },
  {
    Header: "Name",
    // headerClassName: "px-4 text-left",
    className: "px-3 text-left",
    accessor: "fullName",
    width: 375,
    Cell: ({ original, value }) => {
      return (
        <div className="grid grid-cols-3 gap-4">
          <div className="col-span-2">
            <StyledLink
              className={`vt-highlight ${
                original?.isBlocked ? "text-red-500" : ""
              }`}
              to={`/d/registration/vendors/details/${original.id}`}
            >
               <div className="flex items-center gap-2 font-500">
              {value}{" "}
              {original?.isBlocked && (
                <Prohibit weight="fill" size={20} color="red" />
              )}
              </div>
            </StyledLink>
            <p className="mb-0 text-xs vt-muted">
              {original.customerId || "NA"}
            </p>
          </div>
          <div>
            <NewTag createdAt={original.createdAt} />
          </div>
        </div>
      )
    },
  },
  {
    Header: "Vendor Types",
    accessor: "groups",
    width: 220,
    Cell({ value: groups }) {
      const types = groups?.filter(g => g?.category?.codename === "RTL")
      return <OverFlowMenu intent="primary" items={types} />
    },
  },
  {
    width: 150,
    Header: "Account",
    accessor: "accounts.0",
    Cell({ value }) {
      const balance = value?.balance
      return (
        <DataCell>
          <Title>
            {typeof balance === "number" ? (
              <span
                className={clsx(
                  balance >= 0 ? "text-green" : "text-red",
                  "font-500"
                )}
              >
                {inr(balance)}
              </span>
            ) : (
              "na"
            )}
          </Title>
          {value?.accountType?.name && (
            <Subtitle>{value?.accountType?.name}</Subtitle>
          )}
        </DataCell>
      )
    },
  },
  {
    Header: "Mobile Number",
    accessor: "number",
    width: 150,
  },
  {
    width: 300,
    Header: "Address",
    accessor: "address",
    Cell({ value }) {
      const formatted = value?.district?.split(",")

      return (
        <div className="vt-text-left">
          <PopoverTooltip
            trigger={
              <div style={{ cursor: "pointer" }}>
                <div className="vt-muted">{value?.village}</div>
              </div>
            }
          >
            <div className="p-2 text-xs">
              <div className="text-sm vt-black mb-1">Address Details</div>
              {formatted?.map((al, idx) => {
                return (
                  <p key={idx} className="mb-0">
                    {al},
                  </p>
                )
              })}
            </div>
          </PopoverTooltip>
        </div>
      )
    },
  },
]
const Vendors = () => {
  const { data, loading } = useVendorList()
  return (
    <div className="rg-vendors">
      <VendorHeader />
      <ActiveFilters />
      <AddAccountTransaction />
      <VendorTransactionHistory />
      <CustomReactTable
        pageSize={13}
        loading={loading}
        data={data?.usersByGroup ?? []}
        loadingText="Fetching Vendors.."
        columns={COLUMNS}
        getTrProps={(_, rowInfo) => {
          if (rowInfo && rowInfo.original) {
            return rowInfo.original?.isBlocked
              ? {
                  className: "bg-red-50",
                }
              : {}
          }
          return {}
        }}
      />
    </div>
  )
}

export default Vendors
